import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { formatApiError } from "../services/api";

/**
 * Lightweight fetching hook for queue-style pages (delivery + collection).
 * Not React Query — we want visibility-change refreshes and manual reloads
 * without adding a library dependency.
 */
export function useQueue({ fetcher, tError }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState([]);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page,
        page_size: pageSize,
      };
      if (search.trim()) params.search = search.trim();
      if (statusFilter.length) params.status = statusFilter.join(",");
      const d = await fetcher(params);
      setItems(d.items || []);
      setTotal(d.total || 0);
    } catch (err) {
      const msg = formatApiError(err, tError);
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [fetcher, page, pageSize, search, statusFilter, tError]);

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === "visible") reload();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [reload]);

  return {
    items,
    total,
    loading,
    error,
    page,
    pageSize,
    search,
    statusFilter,
    setPage,
    setPageSize: (n) => {
      setPageSize(n);
      setPage(1);
    },
    setSearch: (v) => {
      setSearch(v);
      setPage(1);
    },
    setStatusFilter: (arr) => {
      setStatusFilter(arr);
      setPage(1);
    },
    reload,
  };
}
